import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AdminSidebar from '../../components/ui/AdminSidebar';
import DealershipTable from './components/DealershipTable';
import DealershipCard from './components/DealershipCard';
import DealershipForm from './components/DealershipForm';
import PreviewModal from './components/PreviewModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import StatsCards from './components/StatsCards';
import { dealershipService } from '../../services/dealershipService';
import { generateVerificationToken } from '../../services/dealershipService';
import { useMobileGestures, useViewport, usePullToRefresh } from '../../hooks/useMobileGestures';

import { useAuth } from '../../contexts/AuthContext';

const DealershipManagement = () => {
  const navigate = useNavigate();
  const viewport = useViewport();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDealership, setEditingDealership] = useState(null);
  const [previewDealership, setPreviewDealership] = useState(null);
  const [deletingDealership, setDeletingDealership] = useState(null);
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pull to refresh functionality
  const { isPulling, pullDistance, onTouchStart, onTouchMove, onTouchEnd } = usePullToRefresh(
    async () => {
      await loadDealerships();
    }
  );

  // Swipe gestures for mobile navigation
  const swipeGestures = useMobileGestures({
    onSwipeLeft: () => {
      if (viewport?.isMobile && !showForm) {
        // Could navigate to next screen or show sidebar
        console.log('Swipe left detected');
      }
    },
  });

  // Load dealerships from database
  useEffect(() => {
    loadDealerships();
  }, []);

  const loadDealerships = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await dealershipService?.getAll();
      setDealerships(data);
    } catch (err) {
      setError(err?.message || 'Failed to load dealerships');
      console.error('Error loading dealerships:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDealerships = dealerships?.filter((dealership) =>
  dealership?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  dealership?.city?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  dealership?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleAddDealership = () => {
    setEditingDealership(null);
    setShowForm(true);
  };

  const handleEditDealership = (dealership) => {
    setEditingDealership(dealership);
    setShowForm(true);
  };

  const handleGenerateToken = async (dealership) => {
    try {
      const result = await generateVerificationToken(dealership?.id);
      
      if (result?.success) {
        // Copy to clipboard
        await navigator?.clipboard?.writeText(result?.verificationUrl);
        
        alert(
          `✅ Verifizierungs-Link generiert und in Zwischenablage kopiert!\n\n` +
          `Landing Page: www.gridbalance.de/${dealership?.urlSlug}\n` +
          `Verifizierungs-URL: ${result?.verificationUrl}\n\n` +
          `Dieser Link ist 1 Jahr gültig und ermöglicht Code-Verifizierung ohne Anmeldung.`
        );
      } else {
        throw new Error(result?.error);
      }
    } catch (error) {
      console.error('Error generating token:', error);
      alert('❌ Fehler beim Generieren des Verifizierungs-Links: ' + error?.message);
    }
  };

  const handleSaveDealership = async (dealershipData) => {
    try {
      setError('');
      if (editingDealership) {
        await dealershipService?.update(editingDealership?.id, dealershipData);
        // Audit log handled by database trigger
      } else {
        await dealershipService?.create(dealershipData);
        // Audit log handled by database trigger
      }
      await loadDealerships();
      setShowForm(false);
      setEditingDealership(null);
    } catch (err) {
      setError(err?.message || 'Failed to save dealership');
      console.error('Error saving dealership:', err);
    }
  };

  const handleDeleteDealership = (dealership) => {
    setDeletingDealership(dealership);
  };

  const confirmDelete = async () => {
    try {
      setError('');
      await dealershipService?.delete(deletingDealership?.id);
      // Audit log handled by database trigger
      await loadDealerships();
      setDeletingDealership(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete dealership');
      console.error('Error deleting dealership:', err);
    }
  };

  const handleToggleStatus = async (dealership) => {
    try {
      setError('');
      const newStatus = dealership?.status === 'active' ? 'inactive' : 'active';
      
      await dealershipService?.update(dealership?.id, {
        ...dealership,
        status: newStatus
      });
      // Audit log handled by database trigger
      
      await loadDealerships();
      
      // Show success message
      const statusText = newStatus === 'active' ? 'aktiviert' : 'deaktiviert';
      alert(`✅ Partner "${dealership?.name}" wurde erfolgreich ${statusText}.`);
    } catch (err) {
      setError(err?.message || 'Failed to update dealership status');
      console.error('Error toggling dealership status:', err);
    }
  };

  const handlePreview = (dealership) => {
    setPreviewDealership(dealership);
  };

  const handleViewLandingPage = (dealership) => {
    if (dealership?.urlSlug) {
      window.open(`/${dealership?.urlSlug}`, '_blank');
    } else {
      alert('URL-Slug nicht verfügbar. Bitte speichern Sie den Partner zuerst.');
    }
  };

  const handleViewVoucherPortal = (dealership) => {
    navigate('/voucher-verification', { state: { dealership } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground mobile-body">Loading dealerships...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-background"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      {...swipeGestures}
    >
      {/* Pull to refresh indicator */}
      {isPulling && (
        <div 
          className="pull-refresh-indicator"
          style={{ transform: `translateY(${Math.min(pullDistance, 60)}px)` }}
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      <AdminSidebar />
      <div className="lg:pl-60">
        <div className="mobile-padding space-y-4 sm:space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 sm:p-4 touch-feedback-subtle">
              <div className="flex items-start mobile-gap-sm">
                <Icon name="AlertCircle" size={viewport?.isMobile ? 18 : 20} color="var(--color-destructive)" />
                <div className="flex-1">
                  <p className="mobile-caption font-medium text-destructive">Error</p>
                  <p className="mobile-caption text-destructive/80 mt-1">{error}</p>
                </div>
                <button 
                  onClick={() => setError('')}
                  className="touch-target p-1 rounded hover:bg-destructive/20 transition-smooth touch-feedback"
                  aria-label="Close error"
                >
                  <Icon name="X" size={16} color="var(--color-destructive)" />
                </button>
              </div>
            </div>
          )}

          {/* Mobile-optimized header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div>
              <h1 className="mobile-heading text-foreground">
                Partner-Verwaltung
              </h1>
              <p className="mobile-body text-muted-foreground mt-1">
                {viewport?.isMobile ? 'Ihre Partnerschaften' : 'Verwalten Sie Ihre Partnerschaften und Landing Pages'}
              </p>
            </div>
            <Button
              variant="default"
              size={viewport?.isMobile ? 'default' : 'lg'}
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddDealership}
              className="touch-target-lg w-full sm:w-auto"
            >
              {viewport?.isMobile ? 'Hinzufügen' : 'Partner hinzufügen'}
            </Button>
          </div>

          <StatsCards dealerships={dealerships} />

          {/* Mobile-optimized search and table container */}
          <div className="mobile-card">
            <div className="pb-4 border-b border-border">
              <div className="flex flex-col mobile-gap-sm sm:flex-row">
                <div className="flex-1">
                  <Input
                    type="search"
                    placeholder={viewport?.isMobile ? 'Suchen...' : 'Partner suchen...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="touch-target"
                  />
                </div>
                <div className="flex items-center mobile-gap-sm mobile-caption text-muted-foreground">
                  <Icon name="Building2" size={viewport?.isMobile ? 16 : 18} />
                  <span>{filteredDealerships?.length} Partner</span>
                </div>
              </div>
            </div>

            <div className={viewport?.isMobile ? 'pt-4' : 'p-6'}>
              {filteredDealerships?.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Icon 
                    name="Building2" 
                    size={viewport?.isMobile ? 40 : 48} 
                    color="var(--color-muted-foreground)" 
                    className="mx-auto mb-4" 
                  />
                  <p className="mobile-subheading text-foreground mb-2">
                    Keine Partner gefunden
                  </p>
                  <p className="mobile-body text-muted-foreground mb-4 sm:mb-6">
                    {searchQuery ? 'Versuchen Sie eine andere Suche' : 'Fügen Sie Ihren ersten Partner hinzu'}
                  </p>
                  {!searchQuery && (
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={handleAddDealership}
                      className="touch-target-lg w-full sm:w-auto"
                    >
                      {viewport?.isMobile ? 'Hinzufügen' : 'Partner hinzufügen'}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {/* Desktop table view */}
                  <div className="hidden lg:block">
                    <DealershipTable
                      dealerships={filteredDealerships}
                      onEdit={handleEditDealership}
                      onDelete={handleDeleteDealership}
                      onToggleStatus={handleToggleStatus}
                      onPreview={handlePreview}
                      onViewLandingPage={handleViewLandingPage}
                      onViewVoucherPortal={handleViewVoucherPortal}
                      onGenerateToken={handleGenerateToken}
                    />
                  </div>

                  {/* Mobile card view with optimized touch targets */}
                  <div className="lg:hidden mobile-card-stack">
                    {filteredDealerships?.map((dealership) => (
                      <DealershipCard
                        key={dealership?.id}
                        dealership={dealership}
                        onEdit={handleEditDealership}
                        onDelete={handleDeleteDealership}
                        onToggleStatus={handleToggleStatus}
                        onPreview={handlePreview}
                        onViewLandingPage={handleViewLandingPage}
                        onViewVoucherPortal={handleViewVoucherPortal}
                        onGenerateToken={handleGenerateToken}
                        isMobile={viewport?.isMobile}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-optimized modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 animate-fade-in">
          <div className={viewport?.isMobile ? 'mobile-modal' : 'bg-card rounded-lg shadow-modal max-w-2xl w-full'}>
            <div className="mobile-modal-content">
              <div className="mobile-sticky-header flex items-center justify-between mobile-padding">
                {viewport?.isMobile && <div className="bottom-sheet-handle" />}
                <h2 className="mobile-subheading text-foreground">
                  {editingDealership ? 'Bearbeiten' : 'Neu hinzufügen'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingDealership(null);
                  }}
                  className="touch-target p-2 rounded-lg hover:bg-muted transition-smooth touch-feedback"
                  aria-label="Schließen"
                >
                  <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="mobile-padding">
                <DealershipForm
                  dealership={editingDealership}
                  onSave={handleSaveDealership}
                  onCancel={() => {
                    setShowForm(false);
                    setEditingDealership(null);
                  }}
                  isMobile={viewport?.isMobile}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <PreviewModal
        dealership={previewDealership}
        isOpen={!!previewDealership}
        onClose={() => setPreviewDealership(null)}
        isMobile={viewport?.isMobile}
      />

      <DeleteConfirmModal
        dealership={deletingDealership}
        isOpen={!!deletingDealership}
        onConfirm={confirmDelete}
        onCancel={() => setDeletingDealership(null)}
        isMobile={viewport?.isMobile}
      />
    </div>
  );
};

export default DealershipManagement;